/**
 * tracking.js — Helpers de conversion centralisés pour Google Ads + Meta Pixel.
 *
 * Tous les événements de conversion du site passent par ces fonctions pour :
 *   1. Garantir la cohérence (mêmes IDs, mêmes formats)
 *   2. Ajouter automatiquement un transaction_id unique (déduplication Google)
 *   3. Hacher les données utilisateur pour les "Enhanced Conversions" Google
 *   4. Vérifier que gtag/fbq sont chargés avant de tirer (anti-perte d'événements)
 *   5. Logger en console pour debugging
 */

// ━━━ IDs ━━━
const GOOGLE_ADS_ID = 'AW-18176351400';
const GOOGLE_CONVERSION_LABEL = 'QynxCLLrkLAcEKi5lNtD';
const GOOGLE_CONVERSION_TARGET = `${GOOGLE_ADS_ID}/${GOOGLE_CONVERSION_LABEL}`;

// ━━━ Helpers ━━━

// Génère un transaction_id unique (UUID-like, pas besoin de crypto pour ce cas)
function generateTransactionId() {
  return 'txn_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

// SHA-256 d'une string (pour Enhanced Conversions Google Ads)
async function sha256(str) {
  if (!str) return undefined;
  try {
    const buf = new TextEncoder().encode(String(str).trim().toLowerCase());
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return undefined;
  }
}

// Attend que gtag/fbq soit chargé (max 3 secondes)
function waitForGtag(timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (typeof window.gtag === 'function') return resolve(true);
    const start = Date.now();
    const interval = setInterval(() => {
      if (typeof window.gtag === 'function') {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        console.warn('[tracking] gtag pas chargé après', timeoutMs, 'ms');
        resolve(false);
      }
    }, 100);
  });
}

function waitForFbq(timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (typeof window.fbq === 'function') return resolve(true);
    const start = Date.now();
    const interval = setInterval(() => {
      if (typeof window.fbq === 'function') {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

// ━━━ Conversions principales ━━━

/**
 * Déclenche une conversion Lead (formulaire envoyé, email capturé, etc.).
 * À appeler après que l'API ait répondu OK.
 *
 * @param {Object} opts
 * @param {string} opts.source - ex: 'guide-pdf', 'diagnostic', 'simulateur', 'contact'
 * @param {number} opts.value - valeur monétaire estimée du lead (1-50€)
 * @param {string} [opts.email] - email du lead (pour Enhanced Conversions)
 * @param {string} [opts.phone] - téléphone (pour Enhanced Conversions)
 * @param {string} [opts.name] - nom (pour Enhanced Conversions)
 * @returns {Promise<{transactionId: string, ok: boolean}>}
 */
export async function trackLead({ source = 'unknown', value = 1, email, phone, name }) {
  const transactionId = generateTransactionId();
  const ok = await waitForGtag(3000);

  // ━━━ Enhanced Conversions : envoie user_data hashé AVANT le conversion event ━━━
  if (ok && email) {
    try {
      const userData = {};
      const hashedEmail = await sha256(email);
      if (hashedEmail) userData.sha256_email_address = hashedEmail;
      if (phone) {
        const hashedPhone = await sha256(String(phone).replace(/\s+/g, ''));
        if (hashedPhone) userData.sha256_phone_number = hashedPhone;
      }
      if (name) {
        const parts = String(name).trim().split(' ');
        const firstName = parts[0];
        const lastName = parts.slice(1).join(' ');
        if (firstName) userData.sha256_first_name = await sha256(firstName);
        if (lastName) userData.sha256_last_name = await sha256(lastName);
      }
      window.gtag('set', 'user_data', userData);
    } catch (err) {
      console.warn('[tracking] Enhanced conversion hash failed:', err);
    }
  }

  // ━━━ Conversion Google Ads ━━━
  if (ok) {
    try {
      window.gtag('event', 'conversion', {
        send_to: GOOGLE_CONVERSION_TARGET,
        value: value,
        currency: 'EUR',
        transaction_id: transactionId,
      });
      window.gtag('event', 'generate_lead', {
        event_category: 'engagement',
        event_label: source,
        value: value,
      });
      console.log('[tracking] ✅ Lead conversion fired:', { source, value, transactionId });
    } catch (err) {
      console.error('[tracking] gtag conversion failed:', err);
    }
  } else {
    console.warn('[tracking] ⚠️ Lead conversion SKIPPED (gtag not loaded)');
  }

  // ━━━ Meta Pixel Lead ━━━
  const fbqOk = await waitForFbq(2000);
  if (fbqOk) {
    try {
      window.fbq('track', 'Lead', {
        content_name: source,
        content_category: 'Lead form',
        value: value,
        currency: 'EUR',
      }, { eventID: transactionId });
      console.log('[tracking] ✅ Meta Pixel Lead fired');
    } catch (err) {
      console.warn('[tracking] fbq Lead failed:', err);
    }
  }

  return { transactionId, ok };
}

/**
 * Déclenche un InitiateCheckout (clic sur bouton Stripe).
 * À appeler dans le onClick AVANT redirection vers Stripe.
 */
export function trackCheckout({ serviceName, priceStr, email }) {
  const price = parseFloat((priceStr || '').replace(/[^\d.]/g, '')) || 0;
  const transactionId = generateTransactionId();

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'begin_checkout', {
        currency: 'EUR',
        value: price,
        transaction_id: transactionId,
        items: [{ item_name: serviceName, price }],
      });
      console.log('[tracking] ✅ begin_checkout fired:', { serviceName, price, transactionId });
    }
  } catch (e) {
    console.error('[tracking] begin_checkout failed:', e);
  }

  try {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', {
        content_name: serviceName,
        value: price,
        currency: 'EUR',
      }, { eventID: transactionId });
    }
  } catch (e) {}

  return { transactionId };
}

/**
 * Déclenche la conversion page de remerciement (URL-based).
 * À appeler dans useEffect de la page /merci — c'est la conversion la plus fiable
 * pour Google Ads (il préfère les conversions URL-based aux event-based).
 */
export async function trackThankYouPage({ source = 'merci', value = 1, email, name } = {}) {
  const transactionId = generateTransactionId();
  const ok = await waitForGtag(3000);

  if (ok && email) {
    try {
      const userData = { sha256_email_address: await sha256(email) };
      if (name) {
        const parts = String(name).trim().split(' ');
        if (parts[0]) userData.sha256_first_name = await sha256(parts[0]);
      }
      window.gtag('set', 'user_data', userData);
    } catch (err) {}
  }

  if (ok) {
    window.gtag('event', 'conversion', {
      send_to: GOOGLE_CONVERSION_TARGET,
      value,
      currency: 'EUR',
      transaction_id: transactionId,
    });
    console.log('[tracking] ✅ Thank-you page conversion fired:', { source, value, transactionId });
  }

  const fbqOk = await waitForFbq(2000);
  if (fbqOk) {
    try {
      window.fbq('track', 'Lead', {
        content_name: source,
        value,
        currency: 'EUR',
      }, { eventID: transactionId });
    } catch (err) {}
  }

  return { transactionId, ok };
}
